import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useNavigate } from 'react-router-dom';
import { StatusBadge, TableList, Typography, TableListActions } from 'seagull';
import { Eye } from 'seagull/icons';
import { ClientPreview } from '~/components/ClientPreview/ClientPreview';
import { useProposalRowMenuItems } from '~/components/ProposalActions/ProposalRowMenuItems';
import { useOverlayContext, useStore } from '~/contexts';
import { useDownloadAgreementPdf } from '~/hooks/relationship/useDownloadAgreementPdf';
import { useLocaleKeys } from '~/i18n';
import { relationshipSummaryModel, timeModel } from '~/models';
import { isExpired } from '~/models/relationship-summary';
import { selectors } from '~/store/state-selectors';
import { getStatusUIData, getRowAndMainAction } from './ProposalsDashboard.utils';
export default function ProposalsDashboardRow(props) {
    const { proposal, isVendor, index, refresh, onViewActivityLog, onViewProposal, proposalActions } = props;
    const { t: proposalTexts } = useLocaleKeys('pages.proposalsDashboard');
    const expired = isExpired(proposal);
    const isViewMode = useStore(selectors.isViewMode);
    const navigate = useNavigate();
    const overlayContext = useOverlayContext();
    const { downloadAgreementPdf } = useDownloadAgreementPdf();
    const heapId = proposal.isSample ? 'test-proposal' : 'proposals';
    const opposingPartyName = relationshipSummaryModel.opposingPartyName(proposal, isVendor);
    const { statusText, badgeSkin, lastUpdateText } = getStatusUIData(proposal, isVendor, proposalTexts);
    const sellerFullName = relationshipSummaryModel.sellerFullName(proposal);
    const { rowProps, mainAction } = getRowAndMainAction({
        proposal,
        isVendor,
        isViewMode,
        heapId,
        navigate,
        openClientPreview,
        texts: proposalTexts
    });
    const displayedTitle = relationshipSummaryModel.displayedTitle(proposal, isVendor);
    const onDownloadPDF = (p)=>{
        downloadAgreementPdf(p.id);
    };
    const menuRow = {
        id: proposal.id,
        draftId: proposal.draftId,
        isSample: proposal.isSample,
        vendorId: proposal.vendor.id,
        customerId: proposal.customer.id,
        progressStatus: proposal.progressStatus
    };
    const menuItems = useProposalRowMenuItems({
        proposal: menuRow,
        isVendor,
        expired,
        proposalActions,
        approveOnBehalf: {
            withdrawnForEditing: proposal.withdrawnForEditing,
            hasExplicitApprovalToS: proposal.hasExplicitApprovalToS,
            hasServiceBundles: proposal.hasServiceBundles,
            hasOptionalServices: proposal.hasOptionalServices,
            hasBilledImmediatelyService: proposal.hasBilledImmediatelyService
        },
        onViewProposal,
        onViewActivityLog,
        onDownloadPDF,
        refresh
    });
    function openClientPreview() {
        overlayContext.showModal({
            biName: 'client_preview_modal',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (props)=>/*#__PURE__*/ _jsx(ClientPreview, _object_spread({
                    type: "relationshipId",
                    relationshipId: proposal.id
                }, props))
        });
    }
    return /*#__PURE__*/ _jsx(TableList.Row, _object_spread_props(_object_spread({
        intent: mainAction.intent
    }, rowProps), {
        rowId: proposal.id,
        items: [
            {
                value: /*#__PURE__*/ _jsxs(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    testId: "proposals-row-".concat(index, "-name"),
                    children: [
                        opposingPartyName,
                        displayedTitle && /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            variant: "paragraph2",
                            skin: "secondary",
                            children: [
                                "\xa0(",
                                displayedTitle,
                                ")"
                            ]
                        })
                    ]
                }),
                mobileValue: /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    weight: "normal",
                    children: opposingPartyName
                }),
                mobileSecondaryValue: displayedTitle && /*#__PURE__*/ _jsx(Typography, {
                    ellipsis: true,
                    skin: "secondary",
                    children: displayedTitle
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    children: proposal.createdOn ? timeModel.formatDate(proposal.createdOn) : ''
                })
            },
            {
                value: sellerFullName != null && /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    children: sellerFullName
                })
            },
            {
                value: /*#__PURE__*/ _jsx(StatusBadge, {
                    skin: badgeSkin,
                    info: lastUpdateText || undefined,
                    testId: "row-".concat(index, "-status"),
                    children: statusText
                })
            }
        ],
        actionColumn: /*#__PURE__*/ _jsx(TableListActions, {
            bi: {
                button: {
                    name: 'proposals_primary_action'
                },
                menuTrigger: {
                    name: 'proposals_actions'
                }
            },
            buttonText: mainAction.buttonText,
            buttonIntent: mainAction.intent,
            testId: proposal.isSample ? 'test-proposal' : "proposals-row-".concat(index),
            btnHeapId: mainAction.btnHeapId,
            menuHeapId: "".concat(heapId, "-menu-btn"),
            onClickButton: mainAction.onClickButton,
            buttonIcon: /*#__PURE__*/ _jsx(Eye, {}),
            mobile: {
                title: opposingPartyName,
                subtitle: displayedTitle
            },
            children: menuItems.length > 0 && menuItems
        }),
        testId: proposal.isSample ? 'test-proposal' : "proposals-row-".concat(index),
        "data-heapid": heapId
    }), proposal.id);
}
